import {
	type GetObjectCommandOutput,
	GetObjectCommand,
	HeadObjectCommand,
	type HeadObjectCommandOutput,
	S3Client,
} from "@aws-sdk/client-s3";
import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Toast } from "@capacitor/toast";

const getObjectBlob = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<Blob> => {
	const command = new GetObjectCommand({
		Bucket: bucket,
		Key: key,
	});

	const response = await s3Client.send(command);
	if (!response.Body)
		throw new Error("File not found or empty on the Bucket.");

	return await convertObjectToBlob(response);
};

const getObjectHead = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<HeadObjectCommandOutput> => {
	const command = new HeadObjectCommand({
		Bucket: bucket,
		Key: key,
	});

	const response = await s3Client.send(command);
	if (!response) throw new Error("File not found on the Bucket.");

	return response;
};

const convertObjectToStream = (response: GetObjectCommandOutput) => {
	return response.Body as ReadableStream;
};

const convertObjectToBlob = async (response: GetObjectCommandOutput) => {
	return await new Response(convertObjectToStream(response)).blob();
};

const downloadFile = async (name: string, blob: Blob) => {
	if (Capacitor.getPlatform() === "web") {
		downloadFileWeb(name, blob);
	}
	if (
		Capacitor.getPlatform() === "android" ||
		Capacitor.getPlatform() === "ios"
	) {
		downloadFileMobile(name, blob);
	}
};

const downloadFileWeb = (name: string, blob: Blob) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	const formattedName = name.replaceAll(/[\\/@,\s]+/g, "-");
	link.href = url;
	link.download = `${formattedName}`;
	link.click();

	setTimeout(() => {
		link?.parentNode?.removeChild(link);
		window.URL.revokeObjectURL(url);
	}, 0);
};

const downloadFileMobile = async (name: string, blob: Blob) => {
	const formattedName = name.replaceAll(/[\\/@,\s]+/g, "-");
	const arrayBuffer = await blob.arrayBuffer();
	const buffer = new Uint8Array(arrayBuffer);

	await Filesystem.writeFile({
		path: `${formattedName}`,
		data: buffer.toString(),
		directory: Directory.Documents,
		encoding: Encoding.UTF8,
	});

	console.log(`File saved as ${formattedName}.zip in Documents directory`);
	Toast.show({
		text: `File saved as ${formattedName}.zip in Documents directory`,
		duration: "long",
	});
};

export {
	getObjectBlob,
	convertObjectToBlob,
	convertObjectToStream,
	getObjectHead,
	downloadFile,
};
